/**
 * The government expense classes a budget line is grouped under — and, from 06 Oct 2026,
 * the fund-cluster vocabulary an ICS or a PAR is charged against.
 *
 * <b>It is here rather than in the investment-planning slice because TWO suites draw it</b>
 * — the PPA's own line items and the LOGISTICS issuance form's fund cluster — and a logistics
 * component importing from `features/procurement/…` would cross a feature slice to reach
 * three words. That is the `lib/itemIdentity` call: a vocabulary more than one surface
 * renders is defined once, or the two drift and a reader meets one concept written two ways
 * on two screens.
 *
 * <b>The names keep their `PPA_` prefix</b>, and that is deliberate rather than churn left
 * behind: the issuance is BORROWING the PPA's vocabulary rather than coining a second one,
 * and a prefix saying whose words these are is what keeps that visible at the call site.
 *
 * <b>The SERVER has its own copy of the enum</b> (`PpaExpenseClass`). Two copies across the
 * wire are unavoidable — a picker needs its labels before any row arrives — but two on one
 * side is the defect that put "Asset" on a form under an "Equipment" tab.
 */
export const PPA_EXPENSE_CLASS = {
  personnelServices: 0,
  mooe: 1,
  capitalOutlay: 2,
} as const;

export type PpaExpenseClass = (typeof PPA_EXPENSE_CLASS)[keyof typeof PPA_EXPENSE_CLASS];

/** The words, and they must read the same as the server's. */
export const PPA_EXPENSE_CLASS_LABEL: Record<number, string> = {
  [PPA_EXPENSE_CLASS.personnelServices]: 'Personnel Services',
  [PPA_EXPENSE_CLASS.mooe]: 'MOOE',
  [PPA_EXPENSE_CLASS.capitalOutlay]: 'Capital Outlay',
};

/** The short form, for a column header that has no room for "Maintenance and Other…". */
export const PPA_EXPENSE_CLASS_SHORT: Record<number, string> = {
  [PPA_EXPENSE_CLASS.personnelServices]: 'PS',
  [PPA_EXPENSE_CLASS.mooe]: 'MOOE',
  [PPA_EXPENSE_CLASS.capitalOutlay]: 'CO',
};

/**
 * The three in the order the form prints them, for a picker that must not re-sort.
 *
 * `<Select>` sorts alphabetically by default, which would read Capital Outlay, MOOE,
 * Personnel Services — alphabetical order pretending to be the form's own.
 */
export const PPA_EXPENSE_CLASS_ORDER: PpaExpenseClass[] = [
  PPA_EXPENSE_CLASS.personnelServices,
  PPA_EXPENSE_CLASS.mooe,
  PPA_EXPENSE_CLASS.capitalOutlay,
];
